<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: SiteStatsOverview                                     |
| Section: views/overviews                                         |
| Purpose: Site statistics dashboard with tabbed metric sections.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="wrapper overview-wrapper overview-gap-48 stats-dashboard">
        <div class="overview-header">
            <h1>{{ $t('siteStatsOverview.title') }}</h1>
            <p class="subtitle overview-header-text">{{ $t('siteStatsOverview.subtitle') }}</p>
        </div>

        <div v-if="loading" class="dash-status">
            <p>{{ $t('siteStatsOverview.loading') }}</p>
        </div>
        <div v-else-if="error" class="dash-status dash-status--error">
            <p>{{ error }}</p>
        </div>

        <template v-else>
            <!-- Row 1: three donut charts -->
            <div class="dashboard-grid">
                <!-- Geslacht -->
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.gender') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.gender.total) }} {{
                            $t('siteStatsOverview.people') }}</p>
                    </div>
                    <div class="chart-container" role="img"
                        :aria-label="$t('siteStatsOverview.aria.genderDonut', { female: formatNumber(stats.gender.female), male: formatNumber(stats.gender.male), unknown: formatNumber(stats.gender.unknown) })">
                        <DoughnutChart :key="`gender-${chartRefreshKey}`" :chartData="genderChartData"
                            :options="donutOptions" />
                    </div>
                    <ul class="dash-legend" :aria-label="$t('siteStatsOverview.aria.genderLegend')">
                        <li v-for="item in genderLegend" :key="item.label" class="dash-legend-item">
                            <span class="dash-legend-dot" :style="{ background: item.color }" aria-hidden="true"></span>
                            <span class="dash-legend-label">{{ item.label }}</span>
                            <span class="dash-legend-count">{{ formatNumber(item.count) }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Levenden vs. overledenen -->
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.livingVsDeceased') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.livingStatus.total) }} {{
                            $t('siteStatsOverview.people') }}</p>
                    </div>
                    <div class="chart-container" role="img"
                        :aria-label="$t('siteStatsOverview.aria.livingDonut', { alive: formatNumber(stats.livingStatus.alive), deceased: formatNumber(stats.livingStatus.deceased) })">
                        <DoughnutChart :key="`living-${chartRefreshKey}`" :chartData="livingChartData"
                            :options="donutOptions" />
                    </div>
                    <ul class="dash-legend" :aria-label="$t('siteStatsOverview.aria.livingLegend')">
                        <li v-for="item in livingLegend" :key="item.label" class="dash-legend-item">
                            <span class="dash-legend-dot" :style="{ background: item.color }" aria-hidden="true"></span>
                            <span class="dash-legend-label">{{ item.label }}</span>
                            <span class="dash-legend-count">{{ formatNumber(item.count) }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Burgerlijke staat -->
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.maritalStatus') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.maritalStatus.total) }} {{
                            $t('siteStatsOverview.people') }}</p>
                    </div>
                    <div class="chart-container" role="img" :aria-label="$t('siteStatsOverview.aria.maritalDonut')">
                        <DoughnutChart :key="`marital-${chartRefreshKey}`" :chartData="maritalChartData"
                            :options="donutOptions" />
                    </div>
                    <ul class="dash-legend" :aria-label="$t('siteStatsOverview.aria.maritalLegend')">
                        <li v-for="item in maritalLegend" :key="item.label" class="dash-legend-item">
                            <span class="dash-legend-dot" :style="{ background: item.color }" aria-hidden="true"></span>
                            <span class="dash-legend-label">{{ item.label }}</span>
                            <span class="dash-legend-count">{{ formatNumber(item.count) }}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Row 2: three word clouds -->
            <div class="dashboard-grid">
                <!-- Achternamen -->
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.commonSurnames') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.totalUniqueSurnames) }} {{
                            $t('siteStatsOverview.uniqueSurnames') }}
                        </p>
                    </div>
                    <div class="chart-container chart-container--bars" role="img"
                        :aria-label="$t('siteStatsOverview.aria.surnamesBar')">
                        <BarChart :key="`surnames-${chartRefreshKey}`" :chartData="surnamesChartData"
                            :options="barOptions" class="bar-chart-fill" />
                    </div>
                </div>

                <!-- Voornamen mannelijk -->
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.maleFirstNames') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.totalUniqueMaleFirstNames) }} {{
                            $t('siteStatsOverview.uniqueNames') }}
                        </p>
                    </div>
                    <div class="chart-container chart-container--bars" role="img"
                        :aria-label="$t('siteStatsOverview.aria.maleNamesBar')">
                        <BarChart :key="`male-names-${chartRefreshKey}`" :chartData="maleNamesChartData"
                            :options="barOptions" class="bar-chart-fill" />
                    </div>
                </div>

                <!-- Voornamen vrouwelijk -->
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.femaleFirstNames') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.totalUniqueFemaleFirstNames) }} {{
                            $t('siteStatsOverview.uniqueNames') }}
                        </p>
                    </div>
                    <div class="chart-container chart-container--bars" role="img"
                        :aria-label="$t('siteStatsOverview.aria.femaleNamesBar')">
                        <BarChart :key="`female-names-${chartRefreshKey}`" :chartData="femaleNamesChartData"
                            :options="barOptions" class="bar-chart-fill" />
                    </div>
                </div>

                <!-- Meest voorkomende plaatsen -->
                <div class="dash-card dash-card--hidden-temporary">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.commonPlaces') }}</h2>
                        <p class="dash-subtitle">{{ $t('siteStatsOverview.topPlaces') }}</p>
                    </div>
                    <div class="chart-container chart-container--bars" role="img"
                        :aria-label="$t('siteStatsOverview.aria.placesBar')">
                        <BarChart :key="`places-${chartRefreshKey}`" :chartData="placesChartData"
                            :options="barOptions" class="bar-chart-fill" />
                    </div>
                </div>
            </div>

            <div class="dashboard-grid dashboard-grid--half">
                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.ageDistributionLiving') }}</h2>
                        <p class="dash-subtitle">{{ formatNumber(stats.ageDistribution.livingWithBirthDate) }}
                            {{ $t('siteStatsOverview.livingPeopleWithBirthDate') }}</p>
                    </div>
                    <div class="chart_content" role="img" :aria-label="$t('siteStatsOverview.aria.ageHistogram')">
                        <table class="histogram_table age">
                            <tbody>
                                <template v-for="row in ageDistributionRows" :key="row.range">
                                    <tr class="stat row1">
                                        <td class="label_cell">
                                            <div class="label">{{ $t('siteStatsOverview.ages') }} {{ row.range }}
                                            </div>
                                        </td>
                                        <td class="count_cell"
                                            :data-tooltip="$t('siteStatsOverview.aria.ageRangeTooltip', { range: row.range, pct: row.pctLabel })">
                                            <div class="count">{{ formatNumber(row.count) }}</div>
                                        </td>
                                    </tr>
                                    <tr class="stat row2">
                                        <td colspan="2" class="bar_cell"
                                            :data-tooltip="$t('siteStatsOverview.aria.ageRangeTooltip', { range: row.range, pct: row.pctLabel })">
                                            <div class="line">
                                                <div class="bar" :style="{ width: `calc(16px + ${row.pct}%)` }">
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="dash-card">
                    <div class="dash-card-header">
                        <h2>{{ $t('siteStatsOverview.ageAtDeath') }}</h2>
                        <p class="dash-subtitle">{{ $t('siteStatsOverview.deceasedByAgeAndGender') }}</p>
                    </div>
                    <div class="chart_content" role="img" :aria-label="$t('siteStatsOverview.aria.mortalityPyramid')">
                        <div class="chart_averages centered">
                            <div class="header">{{ $t('siteStatsOverview.average') }}</div>
                            <div class="items">
                                <div class="item">
                                    <div class="value">{{ formatNumber(lifeExpectancyAverage.male) }}</div>
                                    <div class="caption">
                                        <div class="legend male"></div>
                                        <div class="label">{{ $t('siteStatsOverview.men') }}</div>
                                    </div>
                                </div>
                                <div class="item">
                                    <div class="value">{{ formatNumber(lifeExpectancyAverage.female) }}</div>
                                    <div class="caption">
                                        <div class="legend female"></div>
                                        <div class="label">{{ $t('siteStatsOverview.women') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <table class="histogram_table genders">
                            <tbody>
                                <template v-for="row in lifeExpectancyRows" :key="row.range">
                                    <tr class="stat row1">
                                        <td class="count_cell male">
                                            <div class="count">{{ formatNumber(row.male) }}</div>
                                        </td>
                                        <td colspan="2" class="label_cell">
                                            <div class="label">{{ $t('siteStatsOverview.ages') }} {{ row.range }}
                                            </div>
                                        </td>
                                        <td class="count_cell female">
                                            <div class="count">{{ formatNumber(row.female) }}</div>
                                        </td>
                                    </tr>
                                    <tr class="stat row2">
                                        <td colspan="2" class="bar_cell male">
                                            <div class="line">
                                                <div class="bar" :style="{ width: `${row.malePct}%` }"></div>
                                            </div>
                                        </td>
                                        <td colspan="2" class="bar_cell female">
                                            <div class="line">
                                                <div class="bar" :style="{ width: `${row.femalePct}%` }"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <SiteStatsGrowth :embedded="true" @ready="refreshAllCharts" />
        </template>
    </div>
    <ContactCta />
</template>

<script>
import { nextTick } from 'vue';
import { DoughnutChart, BarChart } from 'vue-chart-3';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    DoughnutController,
    BarController,
} from 'chart.js';
import { applyGenericSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';
import { fetchPeopleDashboardStats, fetchOverzichtStaticFile } from '@/api/apiService';
import SiteStatsGrowth from '@/views/overviews/SiteStatsGrowth.vue';
import ContactCta from '@/components/ContactCta.vue';
import { useRoute, useRouter } from 'vue-router';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, DoughnutController, BarController);

export default {
    name: 'SiteStatsOverview',
    components: { DoughnutChart, BarChart, SiteStatsGrowth, ContactCta },

    setup() {
        const router = useRouter();
        const route = useRoute();
        return { router, route };
    },

    data() {
        return {
            chartRefreshKey: 0,
            loading: true,
            error: null,
            stats: null,
            chartRefreshTimers: [],
        };
    },

    computed: {
        donutOptions() {
            return {
                responsive: true,
                maintainAspectRatio: true,
                cutout: '65%',
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                                const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
                                return `${ctx.label}: ${this.formatNumber(ctx.raw)} (${pct}%)`;
                            },
                        },
                    },
                },
            };
        },

        genderLegend() {
            return [
                { label: this.$t('siteStatsOverview.legend.female'), color: '#F08080', count: this.stats.gender.female },
                { label: this.$t('siteStatsOverview.legend.male'), color: '#5BC8C8', count: this.stats.gender.male },
                { label: this.$t('siteStatsOverview.legend.unknown'), color: '#D3D3D3', count: this.stats.gender.unknown },
            ];
        },
        genderChartData() {
            return {
                labels: this.genderLegend.map(i => i.label),
                datasets: [{
                    data: this.genderLegend.map(i => i.count),
                    backgroundColor: this.genderLegend.map(i => i.color),
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    hoverOffset: 6,
                }],
            };
        },

        livingLegend() {
            return [
                { label: this.$t('siteStatsOverview.legend.deceased'), color: '#C0C0C0', count: this.stats.livingStatus.deceased },
                { label: this.$t('siteStatsOverview.legend.alive'), color: '#8FBC8F', count: this.stats.livingStatus.alive },
            ];
        },
        livingChartData() {
            return {
                labels: this.livingLegend.map(i => i.label),
                datasets: [{
                    data: this.livingLegend.map(i => i.count),
                    backgroundColor: this.livingLegend.map(i => i.color),
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    hoverOffset: 6,
                }],
            };
        },

        maritalLegend() {
            return [
                { label: this.$t('siteStatsOverview.legend.married'), color: '#B0ACDA', count: this.stats.maritalStatus.married },
                { label: this.$t('siteStatsOverview.legend.single'), color: '#A9A9A9', count: this.stats.maritalStatus.single },
                { label: this.$t('siteStatsOverview.legend.partners'), color: '#F5D76E', count: this.stats.maritalStatus.partners },
            ];
        },
        maritalChartData() {
            return {
                labels: this.maritalLegend.map(i => i.label),
                datasets: [{
                    data: this.maritalLegend.map(i => i.count),
                    backgroundColor: this.maritalLegend.map(i => i.color),
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    hoverOffset: 6,
                }],
            };
        },

        barOptions() {
            return {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                return `${this.formatNumber(ctx.raw)}`;
                            },
                        },
                    },
                },
                onClick: (_, elements, chart) => {
                    if (!elements?.length || !chart?.data?.labels?.length) return;
                    const index = elements[0].index;
                    const selectedLabel = chart.data.labels[index];
                    this.navigateToNameSearch(selectedLabel);
                },
                onHover: (event, elements, chart) => {
                    if (chart?.canvas) {
                        chart.canvas.style.cursor = elements?.length ? 'pointer' : 'default';
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        ticks: {
                            callback: (value) => {
                                return this.formatNumber(value);
                            },
                        },
                    },
                    y: {
                        ticks: {
                            autoSkip: false,
                            font: {
                                size: 11,
                            },
                        },
                    },
                },
            };
        },

        ageDistributionScaleMax() {
            const ranges = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80-89', '90-99', '100+'];
            const values = ranges.map((range) => Number(this.stats?.ageDistribution?.[range] || 0));
            return Math.max(1, ...values);
        },

        ageDistributionRows() {
            const ranges = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80-89', '90-99', '100+'];
            const max = this.ageDistributionScaleMax;
            const total = Number(this.stats?.ageDistribution?.livingWithBirthDate || 0);

            return ranges.map((range) => {
                const count = Number(this.stats?.ageDistribution?.[range] || 0);
                const pct = (count / max) * 100;
                const pctOfTotal = total > 0 ? (count / total) * 100 : 0;
                return {
                    range,
                    count,
                    pct: Number(pct.toFixed(6)),
                    pctLabel: `${pctOfTotal.toFixed(1)}%`,
                };
            });
        },

        topSurnamesToDisplay() {
            if (!this.stats?.topSurnames) return [];
            return this.stats.topSurnames
                .filter(item => !item.name.toLowerCase().includes('onbekend') && !item.name.toLowerCase().includes('vrouw'))
                .slice(0, 25);
        },

        topMaleFirstNamesTodisplay() {
            if (!this.stats?.topMaleFirstNames) return [];
            return this.stats.topMaleFirstNames
                .filter(item => !item.name.toLowerCase().includes('onbekend') && !item.name.toLowerCase().includes('vrouw'))
                .slice(0, 25);
        },

        topFemaleFirstNamesTodisplay() {
            if (!this.stats?.topFemaleFirstNames) return [];
            return this.stats.topFemaleFirstNames
                .filter(item => !item.name.toLowerCase().includes('onbekend') && !item.name.toLowerCase().includes('vrouw'))
                .slice(0, 25);
        },

        surnamesChartData() {
            return {
                labels: this.topSurnamesToDisplay.map(i => i.name),
                datasets: [{
                    data: this.topSurnamesToDisplay.map(i => i.count),
                    backgroundColor: '#5BC8C8',
                    borderRadius: 4,
                    borderSkipped: false,
                }],
            };
        },

        maleNamesChartData() {
            return {
                labels: this.topMaleFirstNamesTodisplay.map(i => i.name),
                datasets: [{
                    data: this.topMaleFirstNamesTodisplay.map(i => i.count),
                    backgroundColor: '#5BC8C8',
                    borderRadius: 4,
                    borderSkipped: false,
                }],
            };
        },

        femaleNamesChartData() {
            return {
                labels: this.topFemaleFirstNamesTodisplay.map(i => i.name),
                datasets: [{
                    data: this.topFemaleFirstNamesTodisplay.map(i => i.count),
                    backgroundColor: '#F08080',
                    borderRadius: 4,
                    borderSkipped: false,
                }],
            };
        },

        placesChartData() {
            if (!this.stats?.topPlaces) return { labels: [], datasets: [{ data: [] }] };
            return {
                labels: this.stats.topPlaces.map(i => i.name),
                datasets: [{
                    data: this.stats.topPlaces.map(i => i.count),
                    backgroundColor: '#A8D5BA',
                    borderRadius: 4,
                    borderSkipped: false,
                }],
            };
        },

        lifeExpectancyScaleMax() {
            const ageRanges = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80-89', '90-99', '100+'];
            if (!this.stats?.lifeExpectancy) return 1;
            const all = ageRanges.flatMap((range) => {
                const group = this.stats.lifeExpectancy[range] || {};
                return [Number(group.male || 0), Number(group.female || 0)];
            });
            return Math.max(1, ...all);
        },

        lifeExpectancyAverage() {
            const ageRanges = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80-89', '90-99', '100+'];
            const midpoint = {
                '0-9': 4.5,
                '10-19': 14.5,
                '20-29': 24.5,
                '30-39': 34.5,
                '40-49': 44.5,
                '50-59': 54.5,
                '60-69': 64.5,
                '70-79': 74.5,
                '80-89': 84.5,
                '90-99': 94.5,
                '100+': 102,
            };

            let maleTotal = 0;
            let femaleTotal = 0;
            let maleWeighted = 0;
            let femaleWeighted = 0;

            ageRanges.forEach((range) => {
                const group = this.stats?.lifeExpectancy?.[range] || {};
                const male = Number(group.male || 0);
                const female = Number(group.female || 0);
                maleTotal += male;
                femaleTotal += female;
                maleWeighted += male * midpoint[range];
                femaleWeighted += female * midpoint[range];
            });

            return {
                male: maleTotal ? Math.round(maleWeighted / maleTotal) : 0,
                female: femaleTotal ? Math.round(femaleWeighted / femaleTotal) : 0,
            };
        },

        lifeExpectancyRows() {
            const ageRanges = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80-89', '90-99', '100+'];
            const maxValue = this.lifeExpectancyScaleMax;
            return ageRanges.map((range) => {
                const group = this.stats?.lifeExpectancy?.[range] || {};
                const male = Number(group.male || 0);
                const female = Number(group.female || 0);
                return {
                    range,
                    male,
                    female,
                    malePct: Math.round((male / maxValue) * 100),
                    femalePct: Math.round((female / maxValue) * 100),
                };
            });
        },

        maxSurnameCount() {
            return this.stats?.topSurnames?.[0]?.count || 1;
        },
        maxMaleNameCount() {
            return this.stats?.topMaleFirstNames?.[0]?.count || 1;
        },
        maxFemaleNameCount() {
            return this.stats?.topFemaleFirstNames?.[0]?.count || 1;
        },
    },

    methods: {
        formatNumber(n) {
            return Number(n).toLocaleString(this.localeTag());
        },
        navigateToNameSearch(name) {
            const query = String(name || '').trim();
            if (!query) return;
            this.router.push({ path: this.localizedPath('/search'), query: { q: query, page: 1 } });
        },
        localeTag() {
            const locale = this.$i18n?.locale?.value ?? this.$i18n?.locale;
            if (locale === 'en') return 'en-US';
            if (locale === 'id') return 'id-ID';
            return 'nl-NL';
        },
        localizedPath(path) {
            const locale = this.route?.params?.locale;
            return locale ? `/${locale}${path}` : path;
        },
        clearChartRefreshTimers() {
            this.chartRefreshTimers.forEach((id) => clearTimeout(id));
            this.chartRefreshTimers = [];
        },
        scheduleChartRefresh() {
            this.clearChartRefreshTimers();

            // Use multiple passes because hidden/background tabs can delay layout.
            this.refreshAllCharts();
            this.chartRefreshTimers.push(setTimeout(() => this.refreshAllCharts(), 120));
            this.chartRefreshTimers.push(setTimeout(() => this.refreshAllCharts(), 360));
        },
        handleVisibilityOrFocusRefresh() {
            if (document.hidden) return;
            this.scheduleChartRefresh();
        },
        refreshAllCharts() {
            nextTick(() => {
                requestAnimationFrame(() => {
                    const instances = Object.values(ChartJS.instances || {});
                    instances.forEach((chart) => {
                        if (!chart) return;
                        try {
                            chart.resize();
                            chart.update('none');
                        } catch (error) {
                            // Ignore stale/destroyed chart instances during tab switches.
                        }
                    });
                });
            });
        },
        async loadStats() {
            this.loading = true;
            this.error = null;
            try {
                try {
                    this.stats = await fetchOverzichtStaticFile();
                } catch {
                    this.stats = await fetchPeopleDashboardStats();
                }
            } catch (err) {
                this.error = this.$t('siteStatsOverview.loadError');
            } finally {
                this.loading = false;
            }
        },
    },

    watch: {
        loading(isLoading) {
            if (!isLoading) {
                this.scheduleChartRefresh();
            }
        }
    },

    mounted() {
        const title = this.$t('siteStatsOverview.seoTitle');
        const description = this.$t('siteStatsOverview.seoDescription');
        const image = 'https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg';
        const url = window.location.href;

        applyGenericSeo({ title, description, image, url }, { includeTwitter: true });
        const statsUrl = this.localizedPath('/statistieken');
        applyBreadcrumbSeo([
            { name: 'Home', url: 'https://sejarah-thumann.online/' },
            { name: this.$t('siteStatsOverview.title'), url: `https://sejarah-thumann.online${statsUrl}` },
        ]);

        window.addEventListener('focus', this.handleVisibilityOrFocusRefresh);
        document.addEventListener('visibilitychange', this.handleVisibilityOrFocusRefresh);

        this.loadStats();
    },

    beforeUnmount() {
        window.removeEventListener('focus', this.handleVisibilityOrFocusRefresh);
        document.removeEventListener('visibilitychange', this.handleVisibilityOrFocusRefresh);
        this.clearChartRefreshTimers();
    },
};
</script>

<style scoped>
/* ─── Grid ─────────────────────────────────────────── */
.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
}

.dashboard-grid--half {
    grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 900px) {
    .dashboard-grid {
        grid-template-columns: 1fr;
    }
}

/* ─── Card ──────────────────────────────────────────── */
.dash-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.dash-card--hidden-temporary {
    display: none;
}

.dash-card-header h2 {
    margin: 0 0 4px;
    font-size: 1rem;
    font-weight: 700;
    color: var(--brand-text-dark);
}

.dash-subtitle {
    margin: 0;
    font-size: 0.85rem;
    color: var(--brand-text-muted, #6f6a63);
}

/* ─── Chart ─────────────────────────────────────────── */
.chart-container {
    position: relative;
    width: 100%;
}

.chart-container--bars {
    height: 560px;
    min-height: 560px;
}

.bar-chart-fill {
    height: 100%;
}

.chart-container--bars :deep(.bar-chart-fill > div) {
    height: 100%;
}

.chart-container--bars :deep(canvas) {
    height: 100% !important;
}

@media (max-width: 900px) {
    .chart-container--bars {
        height: 460px;
        min-height: 460px;
    }
}

.chart_content {
    max-width: 980px;
    width: 100%;
    margin: 0 auto;
}

.chart_averages.centered {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    display: none;
}

.chart_averages .header {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--brand-text-muted, #6f6a63);
    font-weight: 700;
}

.chart_averages .items {
    display: flex;
    gap: 18px;
}

.chart_averages .item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.chart_averages .value {
    font-size: 1.05rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--brand-text-dark);
}

.chart_averages .caption {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.76rem;
    color: var(--brand-text-muted, #6f6a63);
}

.chart_averages .legend {
    width: 10px;
    height: 10px;
    border-radius: 0;
    ;
}

.chart_averages .legend.male {
    background: #5BC8C8;
}

.chart_averages .legend.female {
    background: #F08080;
}

.histogram_table.genders {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 2px;
    table-layout: fixed;
}

.histogram_table.age {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 2px;
    table-layout: fixed;
}

.histogram_table.age .label_cell {
    text-align: left;
    vertical-align: middle;
    padding-right: 8px;
}

.histogram_table.age .label_cell .label {
    font-size: 0.74rem;
    font-weight: 400;
    color: var(--brand-text-muted, #6f6a63);
    line-height: 1.15;
}

.histogram_table.age .count_cell {
    width: 70px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 400;
    color: var(--brand-text-muted, #6f6a63);
    vertical-align: bottom;
    font-size: .74rem;
}

.histogram_table.age .bar_cell {
    height: 24px;
    padding: 0;
}

.histogram_table.age .bar_cell .line {
    position: relative;
    height: 100%;
    width: 100%;
    border-radius: 0;
    ;
    background: rgba(163, 51, 51, 0.16);
    overflow: hidden;
}

.histogram_table.age .bar_cell .bar {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    border-radius: 0;
    ;
    background: var(--brand-color-1, #a33);
}

.histogram_table.genders .count_cell {
    width: 72px;
    font-variant-numeric: tabular-nums;
    font-weight: 400;
    color: var(--brand-text-muted, #6f6a63);
    vertical-align: bottom;
    font-size: 0.74rem;
}

.histogram_table.genders .count_cell.male {
    text-align: left;
    padding-right: 10px;
}

.histogram_table.genders .count_cell.female {
    text-align: right;
    padding-left: 10px;
}

.histogram_table.genders .label_cell {
    text-align: center;
    vertical-align: middle;
}

.histogram_table.genders .label_cell .label {
    font-size: 0.74rem;
    font-weight: 400;
    color: var(--brand-text-muted, #6f6a63);
    line-height: 1.15;
}

.histogram_table.genders .bar_cell {
    position: relative;
    height: 24px;
    padding: 0;
}

.histogram_table.genders .bar_cell.male {
    border-right: 1px solid #fff;
    padding-right: 0;
}

.histogram_table.genders .bar_cell.female {
    padding-left: 0;
}

.histogram_table.genders .bar_cell .line {
    position: relative;
    height: 100%;
    width: 100%;
    border-radius: 0;
    overflow: hidden;
}

.histogram_table.genders .bar_cell.male .line {
    background: rgba(91, 200, 200, 0.2);
}

.histogram_table.genders .bar_cell.female .line {
    background: rgba(240, 128, 128, 0.2);
}

.histogram_table.genders .bar_cell .bar {
    position: absolute;
    top: 0;
    height: 100%;
    border-radius: 0;
    ;
}

.histogram_table.genders .bar_cell.male .bar {
    right: 0;
    background: #5BC8C8;
}

.histogram_table.genders .bar_cell.female .bar {
    left: 0;
    background: #F08080;
}

@media (max-width: 900px) {
    .histogram_table.age .count_cell {
        width: 54px;
    }

    .histogram_table.genders .count_cell {
        width: 52px;
    }

    .histogram_table.genders .count_cell.male {
        padding-right: 6px;
    }

    .histogram_table.genders .count_cell.female {
        padding-left: 6px;
    }
}

/* ─── Legend ────────────────────────────────────────── */
.dash-legend {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.dash-legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
}

.dash-legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dash-legend-label {
    flex: 1;
    color: var(--brand-text-dark);
}

.dash-legend-count {
    font-variant-numeric: tabular-nums;
    color: var(--brand-text-muted, #6f6a63);
}

/* ─── Word cloud ─────────────────────────────────────── */
.wordcloud {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    align-items: baseline;
    padding: 4px 0;
    min-height: 160px;
}

.wordcloud-word {
    line-height: 1.3;
    cursor: default;
    color: var(--brand-text-dark);
    transition: color 0.15s ease;
    font-weight: 500;
}

.wordcloud-word:hover {
    color: var(--brand-color-1, #a33);
}

.wordcloud-word--male {
    color: #3a7abf;
}

.wordcloud-word--male:hover {
    color: #1d5fa0;
}

.wordcloud-word--female {
    color: #b85473;
}

.wordcloud-word--female:hover {
    color: #8f2e50;
}

/* ─── Status ─────────────────────────────────────────── */
.dash-status {
    padding: 32px;
    text-align: center;
    color: var(--brand-text-muted, #6f6a63);
    font-size: 0.95rem;
}

.dash-status--error {
    color: #c0392b;
}
</style>

<style scoped>
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 24px;
}

.stats-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    padding: 24px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
    color: var(--brand-text-dark);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* .stats-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 48px rgba(0, 0, 0, 0.12);
} */

.stats-card--ghost {
    background: rgba(255, 255, 255, 0.5);
    border: 2px dashed rgba(0, 0, 0, 0.25);
    box-shadow: none;
}

.stats-card--ghost:hover {
    transform: none;
    box-shadow: none;
}

.stats-card-header h2 {
    margin: 0 0 8px;
}

.stats-card-header p {
    margin: 0;
    color: var(--brand-text-muted, #6f6a63);
}

.stats-card-actions {
    display: flex;
    justify-content: flex-start;
}

@media (max-width: 860px) {
    .stats-card-actions {
        width: 100%;
    }
}
</style>
